import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import React, { useCallback, useEffect, useState } from 'react'
import { Redirect, useLocation, useParams } from 'react-router-dom'

import { fetchPublicIssueForm, submitPublicIssue } from 'api'
import { navigationPaths } from 'app/config'
import { getLoggedInUserName, isUserAuthenticated } from 'app/helpers'

/**
 * THE CENTRE'S PAGE.
 *
 * Since 2026-09-22 it needs an Alfred login: the raiser's name comes from the
 * account, not a typed field (Satya's to-do, form item e). A visitor without
 * a session is sent to the login page and comes straight back here. The token
 * still fixes the centre — a login does not widen what the link can do.
 *
 * Replaces the "Issue Tracker V2" Google Form the centres use today, so it is
 * laid out the way that form is — one question per card, top to bottom, on a
 * phone at the front desk.
 *
 * The token in the URL is the centre. On the Google Form the centre was a free
 * dropdown of twelve and could be picked wrong; here it is shown, locked, and
 * not submitted by the client at all — the backend reads it from the token.
 */
const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    background: '#f3efe8',
    padding: theme.spacing(2, 1.5, 6),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  stack: { width: '100%', maxWidth: 620 },
  card: {
    background: '#fff',
    borderRadius: 8,
    border: '1px solid #e3ddd3',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.75)
  },
  headerCard: {
    background: '#fff',
    borderRadius: 8,
    borderTop: '10px solid #f4511e',
    border: '1px solid #e3ddd3',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    padding: theme.spacing(3, 3, 2.5),
    marginBottom: theme.spacing(1.75)
  },
  // The brand mark, the way Alfred writes it everywhere else: lowercase,
  // brand orange (theme ohOrange), no letter-spacing (form item d).
  brand: { color: '#F25E35', fontWeight: 800, fontSize: 15, lineHeight: 1 },
  title: { marginTop: theme.spacing(0.5), fontWeight: 600 },
  desc: { marginTop: theme.spacing(1.5), color: '#5f5a52', lineHeight: 1.5 },
  centreRow: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap'
  },
  centreChip: { background: '#fdece5', color: '#b23c17', fontWeight: 700 },
  centreNote: { fontSize: 12.5, color: '#8a8177' },
  question: {
    fontSize: 15,
    fontWeight: 600,
    color: '#26211b',
    marginBottom: theme.spacing(1.5)
  },
  req: { color: '#d93025', marginLeft: 2 },
  hint: { fontSize: 12.5, color: '#8a8177', marginTop: theme.spacing(0.75) },
  input: { maxWidth: 420 },
  half: { maxWidth: 260 },
  // A select's label sits inside the field until it shrinks, and the dropdown
  // caret is painted over that same space — a label long enough to reach it
  // renders text under the arrow. Cap the label so it ellipsizes instead, which
  // holds at any field width, phone included.
  selectField: {
    '& .MuiInputLabel-root': {
      maxWidth: 'calc(100% - 36px)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    '& .MuiInputLabel-shrink': { maxWidth: 'calc(100% / 0.75 - 36px)' }
  },
  fileRow: { marginTop: theme.spacing(0.5) },
  fileName: {
    fontSize: 13,
    color: '#5f5a52',
    marginTop: theme.spacing(0.75),
    wordBreak: 'break-all'
  },
  error: {
    background: '#fdecea',
    color: '#a4231a',
    borderRadius: 8,
    padding: theme.spacing(1.25, 1.5),
    fontSize: 13.5,
    lineHeight: 1.5,
    marginBottom: theme.spacing(1.75)
  },
  warn: {
    background: '#fff3e0',
    color: '#8a4b00',
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
    fontSize: 13,
    lineHeight: 1.5,
    marginTop: theme.spacing(1.5)
  },
  submitBtn: {
    background: '#f4511e',
    color: '#fff',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    '&:hover': { background: '#d84315' },
    '&:disabled': { background: '#e0d8cd', color: '#fff' }
  },
  centered: { textAlign: 'center', padding: theme.spacing(6) },
  doneRef: {
    fontFamily: 'monospace',
    fontSize: 18,
    fontWeight: 700,
    color: '#26211b',
    marginTop: theme.spacing(1)
  },
  threadLink: {
    display: 'inline-block',
    marginTop: theme.spacing(2),
    color: '#1e4fa3',
    fontWeight: 600
  }
}))

const ReportIssuePublic = () => {
  const classes = useStyles()
  const { token } = useParams()
  const location = useLocation()
  const raisedBy = getLoggedInUserName() || ''

  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(null)

  const [values, setValues] = useState({
    category: '',
    description: '',
    source: 'not_tech',
    source_other: '',
    about_type: '',
    about_name: '',
    about_number: ''
  })
  const [files, setFiles] = useState([])

  // Read the value BEFORE the updater runs — React 16 pools synthetic events, so
  // `event.target` is null by the time a functional setState callback executes.
  const set = (key) => (event) => {
    const { value } = event.target
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchPublicIssueForm(token)
      setForm(data)
      setLoadError('')
    } catch (err) {
      // Unknown and inactive tokens answer identically, so this message must not
      // speculate about which one it was.
      setLoadError(
        err?.response?.data?.detail ||
          'This link is not valid. Please ask HQ for your centre’s link.'
      )
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    load()
  }, [load])

  const submit = async () => {
    setSaving(true)
    setError('')
    try {
      const payload = { ...values, screenshots: files }
      const data = await submitPublicIssue(token, payload)
      setDone(data)
      window.scrollTo(0, 0)
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          'Something went wrong sending this. Nothing you typed was lost — ' +
            'please press Raise issue again.'
      )
    } finally {
      setSaving(false)
    }
  }

  const owner = (form?.categories || []).find(
    (category) => category.uuid === values.category
  )?.owner_name

  // No session -> the login page, which returns to this exact link
  // afterwards (LoginPage reads `state.from`). Checked on every render so an
  // expired session mid-form also lands on login rather than on a 401.
  if (!isUserAuthenticated()) {
    return (
      <Redirect
        to={{ pathname: navigationPaths.login, state: { from: location } }}
      />
    )
  }

  if (loading) {
    return (
      <div className={classes.page}>
        <div className={classes.centered}>
          <CircularProgress />
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className={classes.page}>
        <div className={classes.stack}>
          <div className={classes.headerCard}>
            <Typography className={classes.brand}>openhouse</Typography>
            <Typography variant="h6" className={classes.title}>
              Raise an issue
            </Typography>
            <Typography className={classes.desc}>{loadError}</Typography>
          </div>
        </div>
      </div>
    )
  }

  if (done) {
    return (
      <div className={classes.page}>
        <div className={classes.stack}>
          <div className={classes.headerCard}>
            <CheckCircleIcon style={{ color: '#196b3a' }} />
            <Typography variant="h6" className={classes.title}>
              Raised — thank you
            </Typography>
            <Typography className={classes.doneRef}>
              {done.reference}
            </Typography>
            <Typography className={classes.desc}>
              {done.owner_name
                ? `It has gone to your centre’s Slack channel and ${done.owner_name} has been tagged.`
                : 'It has gone to your centre’s Slack channel.'}
            </Typography>
            {done.slack_permalink ? (
              <a
                className={classes.threadLink}
                href={done.slack_permalink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open the Slack thread
              </a>
            ) : null}
            {(done.screenshot_warnings || []).length ? (
              <div className={classes.warn}>
                {done.screenshot_warnings.map((warning) => (
                  <div key={warning}>{warning}</div>
                ))}
              </div>
            ) : null}
          </div>
          <div className={classes.card}>
            <Button
              className={classes.submitBtn}
              onClick={() => {
                setDone(null)
                setFiles([])
                setValues((prev) => ({
                  ...prev,
                  category: '',
                  description: '',
                  about_type: '',
                  about_name: '',
                  about_number: ''
                }))
              }}
            >
              Raise another
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={classes.page}>
      <div className={classes.stack}>
        <div className={classes.headerCard}>
          <Typography className={classes.brand}>openhouse</Typography>
          <Typography variant="h6" className={classes.title}>
            Raise an issue
          </Typography>
          {/* Two facts and nothing else (form item c): where it is filed
              against, and who it will be filed by — both fixed, neither
              typed. */}
          <div className={classes.centreRow}>
            <Chip
              size="small"
              label={form?.centre_name}
              className={classes.centreChip}
            />
            {raisedBy ? (
              <span className={classes.centreNote}>raised by {raisedBy}</span>
            ) : null}
          </div>
        </div>

        {error ? <div className={classes.error}>{error}</div> : null}

        <div className={classes.card}>
          <Typography className={classes.question}>
            What’s it about<span className={classes.req}>*</span>
          </Typography>
          <TextField
            select
            fullWidth
            className={classes.input}
            variant="outlined"
            size="small"
            value={values.category}
            onChange={set('category')}
          >
            {(form?.categories || []).map((category) => (
              <MenuItem key={category.uuid} value={category.uuid}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>
          {owner ? <div className={classes.hint}>Goes to {owner}.</div> : null}
        </div>

        {/* Sits where "Your name" used to (form item b). Optional: only when
            the issue is about one parent, teacher or team member. */}
        <div className={classes.card}>
          <Typography className={classes.question}>
            Parent/Teacher Name
          </Typography>
          <div className={classes.hint} style={{ marginBottom: 12 }}>
            Only if this is about one parent, teacher or team member. Leave
            blank otherwise.
          </div>
          <Box display="flex" flexWrap="wrap" style={{ gap: 12 }}>
            <TextField
              select
              className={`${classes.half} ${classes.selectField}`}
              variant="outlined"
              size="small"
              label="About"
              value={values.about_type}
              onChange={set('about_type')}
              style={{ minWidth: 180 }}
            >
              <MenuItem value="">—</MenuItem>
              {(form?.about_types || []).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className={classes.half}
              variant="outlined"
              size="small"
              label="Their name"
              value={values.about_name}
              onChange={set('about_name')}
            />
            <TextField
              className={classes.half}
              variant="outlined"
              size="small"
              label="Their number"
              value={values.about_number}
              onChange={set('about_number')}
            />
          </Box>
        </div>

        {/* No Title field (form item f): the backend cuts the title from the
            first sentence of this, so the first line should say what it is. */}
        <div className={classes.card}>
          <Typography className={classes.question}>
            Issue description<span className={classes.req}>*</span>
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            variant="outlined"
            size="small"
            placeholder="Start with what is wrong, then the details."
            value={values.description}
            onChange={set('description')}
          />
        </div>

        <div className={classes.card}>
          <Typography className={classes.question}>
            Issue Source<span className={classes.req}>*</span>
          </Typography>
          <TextField
            select
            fullWidth
            className={classes.input}
            variant="outlined"
            size="small"
            value={values.source}
            onChange={set('source')}
          >
            {(form?.sources || []).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {values.source === 'other' ? (
            <Box mt={1.5}>
              <TextField
                fullWidth
                className={classes.input}
                variant="outlined"
                size="small"
                placeholder="Where?"
                value={values.source_other}
                onChange={set('source_other')}
              />
            </Box>
          ) : null}
        </div>

        <div className={classes.card}>
          <Typography className={classes.question}>
            Screenshots or screen recordings
          </Typography>
          <div className={classes.fileRow}>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={(event) =>
                setFiles(Array.from(event.target.files || []).slice(0, 5))
              }
            />
          </div>
          <div className={classes.hint}>
            Up to 5 files, 25 MB each. Images and screen recordings.
          </div>
          {files.map((file) => (
            <div className={classes.fileName} key={file.name}>
              {file.name}
            </div>
          ))}
        </div>

        <div className={classes.card}>
          <Button
            className={classes.submitBtn}
            disabled={saving || !values.category || !values.description.trim()}
            onClick={submit}
          >
            {saving ? 'Sending…' : 'Raise issue'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ReportIssuePublic
